import React, { useEffect, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import { client } from 'application/middleware/ekardo/ekardoClient';
import { GET_USERFLOWS } from 'application/middleware/ekardo/queries/getUserFlow';
import cx from 'classnames';
import Link from 'next/link';

import { ActivityProps, Activity } from 'components/Dashboard/Activities/Activity';
import Modules from 'components/Dashboard/Modules';
import { Grid } from 'components/Grid/Grid';
import LoadingSpinner from 'components/LoadingSpinner';

import { PAGE_HAS_BEEN_VIEWED } from '../ETools/ETools.mutations';

import styles from './Dashboard.module.css';
import Overview from './Overview';

const Activities: ActivityProps[] = [
  {
    hasBeenViewed: false,
    href: '',
    pageId: 121998,
    text: 'Use the calculator',
    title: 'Alc-ulator',
    type: 'Tool',
  },
  {
    hasBeenViewed: false,
    href: '/etools/calendar',
    pageId: 122007,
    text: 'See my entries',
    title: 'Drinking Diary',
    type: 'Tool',
  },
  {
    hasBeenViewed: false,
    href: '/etools/my-experiment',
    pageId: 122268,
    text: 'Manage',
    title: 'My Experiment',
    type: 'Tool',
  },
  {
    hasBeenViewed: false,
    href: '/etools/my-plan',
    pageId: 123410,
    text: 'Manage',
    title: 'My Plan',
    type: 'Tool',
  },
  {
    hasBeenViewed: false,
    href: '/etools/relationship-tool',
    pageId: 122032,
    text: 'See my relationships',
    title: 'Alcohol and Relationships Tool',
    type: 'Tool',
  },
  {
    hasBeenViewed: false,
    href: '/etools/lapse-management-tool',
    pageId: 122043,
    text: 'Manage',
    title: 'Lapse Management Tool',
    type: 'Tool',
  },

  {
    hasBeenViewed: false,
    href: '/etools/planning-scheduling-activity',
    pageId: 122050,
    text: 'Manage',
    title: 'My Scheduled Activities',
    type: 'Tool',
  },
  {
    href: '/etools/video-library',
    text: 'Review',
    title: 'Video Content',
    type: 'Resources',
  },
];

export const getUnlockedActivities = () => {
  return Activities.map((activity) => {
    const { pageId } = activity;

    const { data } = useQuery(PAGE_HAS_BEEN_VIEWED, {
      skip: !pageId,
      client,
      fetchPolicy: 'cache-and-network',
      variables: {
        pageId,
      },
    });

    const hasBeenViewed = data?.contentPage?.hasBeenViewed || !pageId;

    return {
      ...activity,
      hasBeenViewed,
    };
  });
};

export function Dashboard(): JSX.Element {
  const [userExperience, setUserExperience] = useState(0);
  const [userFlows, setUserFlows] = useState(undefined);

  const [executeGetUserFlows, { loading: isGetUserFlowsLoading }] = useLazyQuery(GET_USERFLOWS, {
    client,
    fetchPolicy: 'cache-and-network',
    onCompleted(response) {
      const {
        userFlow: { getUserFlows: returnedUserFlows },
      } = response;

      handleGetUserFlows(returnedUserFlows);
    },
  });

  const transformedActivities = getUnlockedActivities();

  useEffect(() => {
    executeGetUserFlows();
  }, []);

  function getFlattenedContentPages(contentPages) {
    return contentPages.reduce((acc, currentContentPage) => {
      const { contentPageChildren } = currentContentPage;

      acc.push(currentContentPage);

      if (contentPageChildren) {
        getFlattenedContentPages(contentPageChildren).forEach((contentPage) => acc.push(contentPage));
      }

      return acc;
    }, []);
  }

  const LessonsPageId = [
    121998, 121999, 122001, 122002, 122003, 122006, 121996, 122004, 122974, 122007, 122008, 122009, 122010, 122011, 122015, 122017, 122018,
    122020, 122269, 122021, 122024, 122025, 122026, 122027, 122028, 122029, 122030, 122031, 122032, 122035, 122036, 122157, 122037, 122038,
    122042, 122156,
  ];

  function handleGetUserFlows(inputUserFlows) {
    let currentUrl;
    let modulesCompleted = 0;
    let modulesToComplete = 0;
    let primaryPagesToComplete = 0;

    let lessonsCompleteds = [];
    const modules = inputUserFlows.flatMap((userFlow) => {
      const { id: userFlowId, userFlowSteps } = userFlow;

      let currentStep;
      let isPreviousCompleted = false;
      let page;
      let tempExperience = 0;

      const areAllStepsCompleted = userFlowSteps.filter((r) => r.userStatus === 'COMPLETED').length === userFlowSteps.length;

      if (!areAllStepsCompleted) {
        currentStep = userFlowSteps.find((r) => r.userStatus === 'IN_PROGRESS');
        if (!currentStep) {
          currentStep = userFlowSteps.find((r) => r.userStatus === 'AVAILABLE');
          page = currentStep.contentGroup.contentPages[0];
        } else {
          page = currentStep.contentGroup.contentPages
            .sort(function (a, b) {
              return b.sort - a.sort;
            })
            .find((r) => r.hasBeenViewed);

          if (!page) {
            page = currentStep.contentGroup.contentPages[currentStep.contentGroup.contentPages.length - 1];
          }
        }
      } else {
        const lastPageVisited = localStorage.getItem('lastPageVisited');
        if (lastPageVisited) {
          const { pageId, contentGroupId } = JSON.parse(lastPageVisited);
          currentStep = userFlowSteps.find((r) => r.contentGroup.id === contentGroupId);
          page = currentStep?.contentGroup.contentPages.find((r) => r.id === pageId);
        }

        if (!page || !lastPageVisited) {
          currentStep = userFlowSteps[0];
          page = currentStep.contentGroup.contentPages[0];
        }
      }
      currentUrl = `/module/${currentStep.url}/${page.url}`;

      const steps = userFlowSteps
        .filter((userFlowStep) => userFlowStep.contentGroup !== null)
        .map((userFlowStep, index) => {
          const {
            contentGroup,
            isComplete: isUserFlowStepCompleted,
            lastViewedContentPage,
            userStatus,
            url: stepUrl,
            stepName: stepTitle,
          } = userFlowStep;
          const { contentPages, description, title } = contentGroup;

          const flattenedContentPages = getFlattenedContentPages(contentPages);
          const pageCompleted = flattenedContentPages.filter((r) => r.hasBeenViewed === true).length;
          const primaryPagesCompleted = contentPages.filter((r) => r.hasBeenViewed === true).length;

          const lastViewedContentPageIndex = lastViewedContentPage
            ? flattenedContentPages.findIndex((contentPage) => contentPage.id === lastViewedContentPage.id)
            : null;

          const contentPageIndex = lastViewedContentPageIndex || 0;
          const contentPage = flattenedContentPages[contentPageIndex];
          const isCurrent = !isUserFlowStepCompleted && userStatus === 'IN_PROGRESS';
          const percentCompleted = isUserFlowStepCompleted ? 100 : (pageCompleted / flattenedContentPages.length) * 100;

          if (!isUserFlowStepCompleted) {
            modulesToComplete += 1;
          } else {
            modulesCompleted += 1;
          }

          flattenedContentPages.forEach((page) => {
            if (LessonsPageId.some((r) => r === page.id && page.hasBeenViewed)) {
              lessonsCompleteds.push(page.id);
            }
          });

          tempExperience += percentCompleted;
          primaryPagesToComplete += contentPages.length - primaryPagesCompleted;

          const step = {
            contentGroup,
            isFirst: index === 0,
            isPreviousCompleted,
            description,
            userStatus,
            lessonsToComplete: flattenedContentPages.length - pageCompleted,
            lessonsLength: flattenedContentPages.length,
            primaryPagesLength: contentGroup?.contentPages.length,
            primaryPagesToComplete,
            stepTitle,
            href: `/module/${stepUrl}/${contentPage.url}`,
            isCompleted: isUserFlowStepCompleted,
            isCurrent,
            isRecommended: false,
            percentCompleted,
            title,
            userFlowId,
          };

          isPreviousCompleted = isUserFlowStepCompleted;
          return step;
        });

      setUserExperience(tempExperience);
      return steps;
    });

    const transformedUserFlows = inputUserFlows.map((userFlow) => {
      const { id, title, description } = userFlow;
      return {
        id,
        steps: modules.filter((module) => module.userFlowId === id),
        title,
        description,
      };
    });

    transformedUserFlows.modulesToComplete = modulesToComplete;
    transformedUserFlows.lessonsToComplete = LessonsPageId.length - lessonsCompleteds.length;
    transformedUserFlows.modulesCompleted = modulesCompleted;
    transformedUserFlows.lessonsCompleted = lessonsCompleteds.length;
    transformedUserFlows.currentPage = currentUrl;

    setUserFlows(transformedUserFlows);
  }

  function isOdd(number: number): number {
    return number % 2;
  }

  const isLoading = isGetUserFlowsLoading || !userFlows;

  return (
    <>
      {isLoading && <LoadingSpinner />}
      <section className={cx(styles.section, 'p-top--none p-b--none', styles.overview)}>
        <div className={cx('w-100', styles.background)}>
          <div className={cx('p-b--none md-p-t--xl', styles.container)}>
            <Overview
              activities={transformedActivities}
              currentPage={userFlows?.currentPage}
              lessonsCompleted={userFlows?.lessonsCompleted}
              lessonsToComplete={userFlows?.lessonsToComplete}
              modulesCompleted={userFlows?.modulesCompleted}
              modulesToComplete={userFlows?.modulesToComplete}
              userExperience={userExperience}
            />
          </div>
        </div>
      </section>

      {userFlows &&
        userFlows.map((userFlow, index) => {
          const { id, steps, title, description } = userFlow;
          return (
            <section
              key={`user-flow-${id}`}
              className={isOdd(index) ? cx(styles.section, styles.tools) : cx(styles.section, styles.modules)}
            >
              <div className={cx(styles.container, 'm-b--large')}>
                <h2 className="h-color--navy">{title}</h2>
                <h2 className="h5 h-font--primary weight-400  m-b--large">{description}</h2>
                <Modules modules={steps} />
              </div>
            </section>
          );
        })}

      <section key="activities">
        <div className={cx(styles.container, 'm-b--large')}>
          <h2 className="h-color--navy">Activities & Online Tools</h2>
          <h2 className="h5 h-font--primary weight-400 m-b--large">
            Here you have easy access to the activities and online tools that you have unlocked. You can also find these features in the
            <Link href="/etools">
              <span className="cursor-pointer m-l--small text-underline">My Tools and Resources</span>
            </Link>{' '}
            section of the website.
          </h2>

          <Grid>
            {transformedActivities.map((activity) => {
              return <Activity key={activity.href.replace(/\//g, '-')} {...activity} />;
            })}
          </Grid>
        </div>
      </section>
    </>
  );
}
